import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { jsonFetch } from '@/utils/jsonFetch'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

interface StudentDetailData {
  student: {
    id: string
    full_name: string | null
    display_name: string | null
    grade: string | null
    status: string | null
    classroom: { id: string; name: string | null; grade_level: string | null } | null
    guardian_confirmed_at: string | null
    created_at: string | null
  }
  profile: {
    photo_urls: string[]
    descriptor: Record<string, unknown>
    guardian_consent: boolean
    participant_id: string | null
  }
  invites: Array<{
    id: string
    email: string | null
    phone: string | null
    relationship: string | null
    status: string
    expired: boolean
    expires_at: string | null
    accepted_at: string | null
  }>
  guardians: Array<{
    id: string
    full_name: string | null
    email: string | null
    relationship: string | null
    linked_at: string | null
  }>
  recent_updates: Array<{
    id: string
    kind: string
    mood: string | null
    note: string | null
    created_at: string | null
  }>
}

const STATUS_THEME: Record<string, { bg: string; label: string }> = {
  draft: { bg: 'bg-gray-100 text-gray-700', label: 'Draft' },
  pending: { bg: 'bg-amber-100 text-amber-800', label: 'Pending guardian' },
  active: { bg: 'bg-emerald-100 text-emerald-800', label: 'Active' },
  archived: { bg: 'bg-slate-100 text-slate-600', label: 'Archived' },
}

const KIND_LABELS: Record<string, string> = {
  before_school: 'Before school',
  after_school: 'After school',
  general: 'General',
}

function formatDate(value: string | null | undefined): string {
  if (!value) return ''
  try {
    return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return '' }
}

function formatTime(value: string | null | undefined): string {
  if (!value) return ''
  try {
    return new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  } catch { return '' }
}

function DescriptorField({ label, value }: { label: string; value: unknown }) {
  if (!value) return null
  const display = Array.isArray(value) ? value.join(', ') : String(value)
  if (!display) return null
  return (
    <div className="flex gap-2 text-sm">
      <span className="font-medium text-cocoa/60 capitalize">{label.replace(/_/g, ' ')}:</span>
      <span className="text-cocoa">{display}</span>
    </div>
  )
}

export default function TeacherStudentDetail() {
  const { studentId } = useParams<{ studentId: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<StudentDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [noteDraft, setNoteDraft] = useState('')
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteSuccess, setNoteSuccess] = useState<string | null>(null)

  const loadDetail = useCallback(async () => {
    if (!studentId) return
    setLoading(true)
    try {
      const raw = await jsonFetch(`${CORE_API}/api/v1/students/${studentId}/detail`) as any
      setData((raw?.data ?? raw) as StudentDetailData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load student')
    } finally {
      setLoading(false)
    }
  }, [studentId])

  useEffect(() => { loadDetail() }, [loadDetail])

  const handleNoteSubmit = async () => {
    if (!noteDraft.trim() || !studentId) return
    setNoteSaving(true)
    setNoteSuccess(null)
    try {
      await jsonFetch(`${CORE_API}/api/v1/students/ledger/append`, {
        method: 'POST',
        body: JSON.stringify({
          student_id: studentId,
          kind: 'teacher_note',
          payload: { text: noteDraft.trim() },
        }),
      })
      setNoteDraft('')
      setNoteSuccess('Note added')
      setTimeout(() => setNoteSuccess(null), 3000)
    } catch { /* ignore */ } finally {
      setNoteSaving(false)
    }
  }

  if (loading) return <div className="p-6 text-cocoa/60">Loading student...</div>
  if (error) return <div className="p-6 text-brick">{error}</div>
  if (!data) return <div className="p-6 text-cocoa/60">Student not found.</div>

  const { student, profile, invites, guardians, recent_updates } = data
  const statusTheme = STATUS_THEME[student.status || 'draft'] || STATUS_THEME.draft
  const desc = profile.descriptor || {}
  const hasDescriptor = Object.values(desc).some(v => v && (Array.isArray(v) ? v.length > 0 : true))

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <button type="button" className="text-sm text-cocoa/60 hover:text-cocoa" onClick={() => navigate(-1)}>
        ← Back to roster
      </button>

      {/* Header */}
      <div className="rounded-2xl bg-white/85 p-5 shadow-soft">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-cocoa">{student.display_name || student.full_name}</h2>
            <p className="text-sm text-cocoa/60">
              {student.grade ? `Grade ${student.grade}` : ''}
              {student.classroom?.name ? ` • ${student.classroom.name}` : ''}
            </p>
          </div>
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${statusTheme.bg}`}>
            {statusTheme.label}
          </span>
        </div>

        {/* Guardian info */}
        {guardians.length > 0 && (
          <div className="mt-4 border-t border-sand/50 pt-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-cocoa/50 mb-2">Guardians</h4>
            {guardians.map((g) => (
              <div key={g.id} className="flex items-center gap-2 text-sm text-cocoa">
                <span className="font-medium">{g.full_name || g.email}</span>
                {g.relationship && <span className="text-cocoa/50">({g.relationship})</span>}
                {g.linked_at && <span className="text-xs text-cocoa/40">since {formatDate(g.linked_at)}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Pending invites */}
        {invites.filter(i => i.status !== 'accepted').length > 0 && (
          <div className="mt-3 border-t border-sand/50 pt-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-cocoa/50 mb-2">Pending Invites</h4>
            {invites.filter(i => i.status !== 'accepted').map((inv) => (
              <div key={inv.id} className="flex items-center gap-2 text-sm">
                <span className="text-cocoa">{inv.email || inv.phone}</span>
                <span className={`text-xs ${inv.expired ? 'text-brick' : 'text-amber-700'}`}>
                  {inv.expired ? 'Expired' : `Expires ${formatDate(inv.expires_at)}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profile / Appearance (from guardian) */}
      <div className="rounded-2xl bg-white/85 p-5 shadow-soft">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60 mb-3">Student Profile</h3>
        {!hasDescriptor && profile.photo_urls.length === 0 ? (
          <p className="text-sm text-cocoa/50 italic">
            No profile information yet. The guardian can build this from their portal.
          </p>
        ) : (
          <div className="space-y-3">
            {profile.photo_urls.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {profile.photo_urls.slice(0, 3).map((url, i) => (
                  <img key={i} src={url} alt={`Photo ${i + 1}`} className="h-20 w-20 rounded-xl object-cover border border-sand" />
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              <DescriptorField label="age" value={desc.age_approx || desc.age} />
              <DescriptorField label="gender" value={desc.gender_presentation} />
              <DescriptorField label="hair color" value={desc.hair_color} />
              <DescriptorField label="hair style" value={desc.hair_style} />
              <DescriptorField label="hair length" value={desc.hair_length} />
              <DescriptorField label="skin tone" value={desc.skin_tone} />
              <DescriptorField label="eye color" value={desc.eye_color} />
              <DescriptorField label="build" value={desc.build} />
              <DescriptorField label="clothing" value={desc.typical_clothing} />
              <DescriptorField label="features" value={desc.distinguishing_features} />
              <DescriptorField label="traits" value={desc.traits} />
            </div>
          </div>
        )}
      </div>

      {/* Guardian Daily Updates */}
      <div className="rounded-2xl bg-white/85 p-5 shadow-soft">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60 mb-3">Guardian Updates</h3>
        {recent_updates.length === 0 ? (
          <p className="text-sm text-cocoa/50 italic">No updates from guardian yet.</p>
        ) : (
          <div className="space-y-3">
            {recent_updates.map((u) => (
              <div key={u.id} className="rounded-xl bg-white/60 px-3 py-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className="rounded-full bg-sand px-2 py-0.5 text-xs font-medium text-cocoa/70">
                    {KIND_LABELS[u.kind] || u.kind}
                  </span>
                  {u.mood && <span className="text-xs text-cocoa/50">{u.mood}</span>}
                  <span className="ml-auto text-xs text-cocoa/40">{formatTime(u.created_at)}</span>
                </div>
                <p className="text-sm text-cocoa">{u.note}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Teacher Notes */}
      <div className="rounded-2xl bg-white/85 p-5 shadow-soft space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Teacher Notes</h3>
        <textarea
          className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm"
          rows={2}
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="Add a note about this student..."
        />
        <div className="flex items-center justify-end gap-3">
          {noteSuccess && <span className="text-xs text-emerald-700">{noteSuccess}</span>}
          <button
            type="button"
            className="rounded-xl bg-brick px-4 py-2 text-sm font-semibold text-white hover:bg-brickDark disabled:opacity-50"
            onClick={handleNoteSubmit}
            disabled={!noteDraft.trim() || noteSaving}
          >
            {noteSaving ? 'Saving...' : 'Add Note'}
          </button>
        </div>
      </div>
    </div>
  )
}
